import PropTypes from "prop-types";
import { useCallback } from "react";
import { sendTagOnMerchandisingButtonClick } from "app/utils/analytics";
import RelativeLink from "app/pages/.shared/RelativeLink";
import "./Merchandising.scss";
import { stringify } from "qs";
import Countdown from "react-countdown-now";
import MerchCountdown from "app/pages/.shared/MerchCountdown/MerchCountdown";
import IconExternalLink from "app/pages/.shared/IconExternalLink";
import { connect } from "react-redux";
import { getMerchPreviewStartDate } from "app/pages/Merchandising/merchandisingSelector";
import { getPartnerMarketing } from "app/reducers/partnerSelector";
import { MERCH_CTA_POSITION_VALUES } from "app/constants";
import { formatPreviewDateToUTCDate } from "app/utils/utils";
import Button, {
	BUTTON_ICON_ALIGNMENTS,
	BUTTON_SIZES,
	BUTTON_VARIANTS,
} from "app/pages/.shared/form/Button";

const ctaStyle = (ctaPosition, ctaXOffset, ctaYOffset) =>
	({
		[MERCH_CTA_POSITION_VALUES.CENTER]: {
			left: "50%",
			top: "50%",
			transform: "translate(-50%,-50%)",
			paddingBottom: `${ctaYOffset}px`,
			paddingLeft: `${ctaXOffset}px`,
			width: "100%",
		},
		[MERCH_CTA_POSITION_VALUES.SOUTH]: {
			bottom: `${0 + ctaYOffset}px`,
			right: "0px",
			left: `${0 + ctaXOffset}px`,
		},
		[MERCH_CTA_POSITION_VALUES.SOUTH_WEST]: {
			bottom: `${0 + ctaYOffset}px`,
			left: `${0 + ctaXOffset}px`,
		},
		[MERCH_CTA_POSITION_VALUES.SOUTH_EAST]: {
			bottom: `${0 + ctaYOffset}px`,
			right: `${0 + ctaXOffset}px`,
		},
		[MERCH_CTA_POSITION_VALUES.WEST]: {
			top: "50%",
			transform: "translate(0%,-50%)",
			paddingLeft: `${ctaXOffset}px`,
			paddingBottom: `${ctaYOffset}px`,
		},
		[MERCH_CTA_POSITION_VALUES.EAST]: {
			top: "50%",
			right: "0px",
			transform: "translate(0%,-50%)",
			paddingRight: `${ctaXOffset}px`,
			paddingBottom: `${ctaYOffset}px`,
		},
		[MERCH_CTA_POSITION_VALUES.NORTH]: {
			top: `${0 + ctaYOffset}px`,
			right: "0px",
			left: `${0 + ctaXOffset}px`,
		},
		[MERCH_CTA_POSITION_VALUES.NORTH_WEST]: {
			top: `${0 + ctaYOffset}px`,
			left: `${0 + ctaXOffset}px`,
		},
		[MERCH_CTA_POSITION_VALUES.NORTH_EAST]: {
			top: `${0 + ctaYOffset}px`,
			right: `${0 + ctaXOffset}px`,
		},
	})[ctaPosition] || {
		bottom: "20px",
		right: "0px",
		left: "0px",
	};

export const Merchandising = ({
	merchandisingCode,
	forceClickOnPicture = false,
	ctaButtonText,
	ctaButtonColor,
	ctaButtonBackgroundColor,
	imageElement,
	countdownPosition,
	countdownTextColor,
	countdownStartAt,
	countdownEndAt,
	daysInHours,
	endAt,
	redirectUrl,
	position,
	indexCarousel,
	previewStartDate,
	ctaPosition,
	ctaXOffset,
	ctaYOffset,
}) => {
	const handleMerchClick = useCallback(() => {
		sendTagOnMerchandisingButtonClick(merchandisingCode, position, indexCarousel);
	}, [merchandisingCode, position, indexCarousel]);

	const now = Date.now();
	const countdownStartDate = new Date(countdownStartAt).getTime();
	const countdownEndDate = new Date(countdownEndAt).getTime();

	const previewDateTimestamp = formatPreviewDateToUTCDate(previewStartDate);

	const shouldPreviewCountdown =
		previewDateTimestamp &&
		previewDateTimestamp >= countdownStartDate &&
		previewDateTimestamp < countdownEndDate;

	const displayCountdown =
		countdownPosition &&
		(shouldPreviewCountdown ||
			(countdownStartAt &&
				now >= countdownStartDate &&
				(!countdownEndAt || now < countdownEndDate)));

	const to = redirectUrl
		? {
				pathname: redirectUrl,
			}
		: {
				pathname: "/merch",
				search: `?${stringify({ code: merchandisingCode })}`,
			};

	const openNewTab =
		merchandisingCode === "TRAVELDOR" ||
		merchandisingCode === "LRETMOI" ||
		merchandisingCode === "ANNIVERSAIRE" ||
		merchandisingCode === "CLUBMED" ||
		redirectUrl;

	const disableMerchRedirection =
		merchandisingCode === "PAIEMENT4X" || merchandisingCode === "SMARTCODES";

	let countdownStyle = "center";

	if (countdownPosition === "top") {
		countdownStyle = "flex-start";
	} else if (countdownPosition === "bottom") {
		countdownStyle = "flex-end";
	}
	const showCta = ctaButtonText && !disableMerchRedirection;

	const buttonStyle = {
		color: ctaButtonColor,
		backgroundColor: ctaButtonBackgroundColor,
	};

	// if the background color of the button is set to "transparent" in Sanity ...
	if (ctaButtonBackgroundColor === "transparent") {
		// ... we want to hide any box shadow ass well
		buttonStyle.boxShadow = "none";
	}

	return (
		<div className="merchandising">
			<RelativeLink
				style={{
					// cas ou les merchandising est dans un carrousel ou le slide viendrait rediriger vers la page merchandising
					pointerEvents: !forceClickOnPicture && !showCta ? "none" : "auto",
				}}
				to={to}
				onClick={handleMerchClick}
				target={openNewTab ? "_blank" : ""}
			>
				{imageElement}
				{displayCountdown && (
					<div
						className="merchandising__countdown"
						style={{ justifyContent: countdownStyle, color: countdownTextColor }}
					>
						<Countdown
							daysInHours={daysInHours}
							date={endAt}
							zeroPadTime={2}
							zeroPadDays={2}
							renderer={({ formatted: { days, hours, minutes, seconds } }) => (
								<MerchCountdown
									days={days}
									hours={hours}
									seconds={seconds}
									minutes={minutes}
								/>
							)}
						/>
					</div>
				)}
				{showCta && ctaPosition !== MERCH_CTA_POSITION_VALUES.HIDDEN && (
					<div
						className="merchandising__cta"
						style={ctaStyle(ctaPosition, ctaXOffset, ctaYOffset)}
					>
						<Button
							target={openNewTab ? "_blank" : ""}
							style={buttonStyle}
							to={to}
							variant={BUTTON_VARIANTS.FILLED}
							size={BUTTON_SIZES.LARGE}
							disabled={disableMerchRedirection}
							onClick={handleMerchClick}
							className="merchandising__relative-link-cta"
							label={ctaButtonText}
							icon={redirectUrl && <IconExternalLink />}
							alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
						/>
					</div>
				)}
			</RelativeLink>
		</div>
	);
};

Merchandising.propTypes = {
	forceClickOnPicture: PropTypes.bool,
	imageElement: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
	ctaButtonText: PropTypes.string,
	ctaButtonColor: PropTypes.string,
	ctaButtonBackgroundColor: PropTypes.string,
	merchandisingCode: PropTypes.string,
	position: PropTypes.number,
	indexCarousel: PropTypes.number,
	countdownPosition: PropTypes.string,
	countdownTextColor: PropTypes.string,
	countdownStartAt: PropTypes.string,
	countdownEndAt: PropTypes.string,
	daysInHours: PropTypes.bool,
	endAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	redirectUrl: PropTypes.string,
	previewStartDate: PropTypes.string,
	ctaPosition: PropTypes.string,
	ctaXOffset: PropTypes.number,
	ctaYOffset: PropTypes.number,
};

const mapStateToProps = state => {
	const marketing = getPartnerMarketing(state);
	return {
		previewStartDate: getMerchPreviewStartDate(state),
		ctaPosition: marketing?.merchConfig?.ctaPosition || MERCH_CTA_POSITION_VALUES.SOUTH,
		ctaXOffset: marketing?.merchConfig?.ctaXOffset || 0,
		ctaYOffset: marketing?.merchConfig?.ctaYOffset || 20,
	};
};

export default connect(mapStateToProps)(Merchandising);
